import {Formik, FormikProps} from 'formik';
import {forEach, merge, find, map} from 'lodash';
import {useCallback, useContext, useEffect, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {
    InputFormik, TextAreaFormik, PhoneInputFormik, ClientInfoControls, ConfirmModal, AssignmentUserSelectorFormik,
    Button, SelectFormik
} from 'components';
import {EMAIL_MAX_LENGTH} from 'constants/client';
import {CLIENT_CONNECT_AUTO_REMOVE_TRANSLATE, ClientConnectFieldPath} from 'constants/client-connect';
import {UserContext} from 'context';
import {ClientConnectAutoRemove} from 'enums';
import {useUserInfoSchema} from 'hooks';
import {Group} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {ApiError, ClientConnect, UserSummary} from 'types';
import {apiUtils, dateUtils, userInfoUtils} from 'utils';
import {formatDate, getParticipantName} from 'utils/transform';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.CLIENTS_CONNECTIONS;

type FormTemplateProps = {
    open: boolean;
    groups: Group[];
    users: UserSummary[];
    mode: 'create' | 'update';
    loading: boolean;
    creationInfo: {name: string, date: string};
    error?: ApiError;
    onClose: (confirm: boolean, connect: Partial<ClientConnect>) => void;
} & FormikProps<Partial<ClientConnect>>;

const FormTemplate: React.FC<FormTemplateProps> = ({
    open, mode, groups, users, loading, error, values, creationInfo,
    onClose, setFieldValue, setFieldError, submitForm,
}: FormTemplateProps) => {
    const {t} = useTranslation();
    const title = useMemo(() => t(mode === 'create' ? T_KEYS.FORM_DIALOG.CREATE.TITLE : T_KEYS.FORM_DIALOG.UPDATE.TITLE), [mode, t]);

    const submitBtnText = useMemo(() =>
        t(mode === 'create' ? T_KEYS.FORM_DIALOG.CREATE.SUBMIT_BTN : T_KEYS.FORM_DIALOG.UPDATE.SUBMIT_BTN), [mode, t]);

    const cancelBtnText = useMemo(() =>
        t(mode === 'create' ? T_KEYS.FORM_DIALOG.CREATE.CANCEL_BTN : T_KEYS.FORM_DIALOG.UPDATE.CANCEL_BTN), [mode, t]);

    const onModalClose = useCallback((confirm: boolean) => {
        if (confirm) {
            submitForm();
        } else {
            onClose(false, null);
        }
    }, [submitForm, onClose]);

    const autoRemoveOptions = useMemo(() => map(ClientConnectAutoRemove, (value) => ({
        id: value,
        name: t(CLIENT_CONNECT_AUTO_REMOVE_TRANSLATE[value])
    })), [t]);

    const handleContactPaste = useCallback(() => {
        setFieldValue(ClientConnectFieldPath.EMAIL, values.userInfo?.email || null);
        setFieldValue(ClientConnectFieldPath.PHONE, values.userInfo?.phone || null);
    }, [values.userInfo?.email, values.userInfo?.phone, setFieldValue]);

    useEffect(() => {
        if (error) {
            forEach(Object.values(ClientConnectFieldPath), (field: string) => {
                const {helperText} = apiUtils.getInputValidationErrorProps(error, t, field);
                if (helperText) {
                    setFieldError(field, helperText);
                }
            });
        }
    }, [error, setFieldError, t]);

    useEffect(() => {
        const _errors = userInfoUtils.getUserInfoErrors(error, t);

        forEach(_errors, (value, key) => {
            setFieldError(key, value);
        });
    }, [error, setFieldError, t]);

    return (
        <ConfirmModal
            open={open}
            title={title}
            fullWidth
            maxWidth='sm'
            cancelText={cancelBtnText}
            okText={submitBtnText}
            okDisabled={loading}
            onClose={onModalClose}
        >
            {creationInfo &&
                <div className='tw-mb-2.5'>
                    <span className='tw-text-default-dimmed'>{`${t(T_KEYS.FORM_DIALOG.UPDATE.CREATED)}: `}</span>
                    {creationInfo.name && <span className='tw-text-break-word'>{creationInfo.name}</span>}
                    {creationInfo.name && creationInfo.date && <span>{', '}</span>}
                    {creationInfo.date && <span className='tw-whitespace-nowrap'>{creationInfo.date}</span>}
                </div>}

            <div className='tw-mb-9'>
                <AssignmentUserSelectorFormik
                    assignmentSelectorProps={{
                        groups,
                        name: ClientConnectFieldPath.ASSIGNMENT_INFO,
                        groupError: apiUtils.getFieldValidationError(error, ClientConnectFieldPath.GROUP),
                        subgroupError: apiUtils.getFieldValidationError(error, ClientConnectFieldPath.SUBGROUP),
                    }}
                    userSelectorProps={{
                        name: ClientConnectFieldPath.AGENT_EMAIL,
                        context: 'agent',
                        field: 'email',
                        users
                    }}
                />
            </div>

            <ClientInfoControls assignment={values.assignmentInfo} />

            <div className='tw-mb-9'>
                <div className='tw-mb-2'>{t(T_KEYS.FIELDS.CONTACTS)}</div>
                {/* TODO: remove after removing contacts */}
                <div className='tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-6'>
                    <PhoneInputFormik
                        name={ClientConnectFieldPath.PHONE}
                        label={t(T_KEYS.FIELDS.PHONE)}
                    />
                    <InputFormik
                        name={ClientConnectFieldPath.EMAIL}
                        label={t(T_KEYS.FIELDS.EMAIL)}
                        type='email'
                        maxLength={EMAIL_MAX_LENGTH}
                    />
                </div>
                {(values.userInfo?.email || values.userInfo?.phone) &&
                    <Button className='tw-mt-2' onClick={handleContactPaste}>{t(T_KEYS.BUTTONS.PASTE_CONTACTS_FROM_CLIENT_INFO)}</Button>}
                <TextAreaFormik
                    name={ClientConnectFieldPath.COMMENTS}
                    label={t(T_KEYS.FIELDS.COMMENTS)}
                    className='tw-mt-4'
                />
            </div>

            <div className='tw-mb-9'>
                <SelectFormik
                    name={ClientConnectFieldPath.AUTO_REMOVE}
                    placeholder={t(T_KEYS.FIELDS.AUTO_REMOVE)}
                    options={autoRemoveOptions}
                    valueAs='id'
                />
            </div>
        </ConfirmModal>
    );
};

type Props = {
    mode: 'create' | 'update';
    open: boolean;
    loading: boolean;
    connect?: Partial<ClientConnect>;
    error?: ApiError;
    groups: Group[];
    users?: UserSummary[];
    substituteContacts?: boolean;
    onClose: (confirm: boolean, connect: Partial<ClientConnect>) => void;
};

export const ClientConnectFormDialog: React.FC<Props> = ({
    mode, open, loading, error, groups, users, connect, substituteContacts, onClose
}: Props) => {
    const {settings: {common: {timeZone, dateFormat, timeFormat}}} = useContext(UserContext);
    const localConnect: Partial<ClientConnect> = useMemo(() => merge({
        userInfo: userInfoUtils.generateEmpty(),
        assignmentInfo: {
            groupId: '',
            subgroupId: ''
        },
        contacts: {
            email: '',
            phone: ''
        },
        comments: '',
        autoRemove: ClientConnectAutoRemove.Never
    }, connect, substituteContacts ? {
        contacts: {
            phone: connect?.contacts?.phone || connect?.userInfo?.phone || '',
            email: connect?.contacts?.email || connect?.userInfo?.email || '',
        }
    } : null), [connect, substituteContacts]);

    const {getUserInfoFields, getControlOnlyFields} = useUserInfoSchema();

    const [userInfoFields, controlOnlyFields] = useMemo(() => [
        getUserInfoFields(connect?.assignmentInfo),
        getControlOnlyFields(connect?.assignmentInfo)
    ], [getUserInfoFields, getControlOnlyFields, connect?.assignmentInfo]);

    const creationInfo = useMemo(() => {
        if (mode === 'update' && connect) {
            const {createdBy, createdAt} = connect;
            const creator = find(users, {eid: createdBy});
            const name = getParticipantName(creator);
            const formattedDate = formatDate(createdAt, timeZone, dateUtils.getDateTimeFormat(dateFormat, timeFormat));
            return {name, date: formattedDate};
        } else {
            return null;
        }
    }, [connect, dateFormat, timeFormat, mode, timeZone, users]);

    const handleSubmit = useCallback((values: Partial<ClientConnect>) => {
        const updatedConnect = {
            ...values,
            userInfo: userInfoUtils.format(values.userInfo, {
                userInfoFields,
                controlOnlyFields
            }),
            id: localConnect.id,
            contacts: values.contacts ? userInfoUtils.filterNotEmptyProps(values.contacts) : null,
        };
        onClose(true, updatedConnect);
    }, [userInfoFields, controlOnlyFields, localConnect.id, onClose]);

    return (
        <Formik
            enableReinitialize
            initialValues={localConnect}
            validateOnBlur={false}
            validateOnChange={false}
            onSubmit={handleSubmit}
        >
            {(props) => (
                <FormTemplate
                    {...props}
                    open={open}
                    groups={groups}
                    users={users}
                    mode={mode}
                    loading={loading}
                    error={error}
                    creationInfo={creationInfo}
                    onClose={onClose}
                />
            )}
        </Formik>
    );
};
